// src/pages/Dashboard/Admin/Votes/components/VotesTable.jsx

import React from "react";
import {
  FaChartBar,
  FaEdit,
  FaListUl,
  FaPlus,
  FaTrash,
  FaVoteYea,
} from "react-icons/fa";

import {
  formatDateTimeFR,
  getVoteStatusMeta,
  getVoteTypeMeta,
  stripHtml,
  truncateText,
} from "../utils/votesDashboardUtils";

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaVoteYea />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucun vote trouvé
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Modifiez les filtres ou créez un nouveau vote.
        </p>
      </div>
    </section>
  );
}

function ActionButton({ title, onClick, disabled, children, tone = "base" }) {
  const tones = {
    base: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    info: "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300",
    primary:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    violet:
      "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300",
    danger:
      "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.base
      }`}
    >
      {children}
    </button>
  );
}

export default function VotesTable({
  votes,
  loading,
  deletingId,
  onEdit,
  onDelete,
  onQuestions,
  onResults,
}) {
  if (loading) return <SkeletonRows />;
  if (!votes.length) return <EmptyState />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Votes
          </p>

          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Liste des votes & sondages
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Le statut reste visible à gauche. Les actions donnent accès à la
            modification, aux questions, aux résultats et à la suppression.
          </p>
        </div>

        <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
          <table className="w-full min-w-[1160px] table-fixed">
            <colgroup>
              <col className="w-[120px]" />
              <col className="w-[320px]" />
              <col className="w-[110px]" />
              <col className="w-[280px]" />
              <col className="w-[115px]" />
              <col className="w-[115px]" />
              <col className="w-[120px]" />
            </colgroup>

            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
              <tr>
                <th className="sticky left-0 z-30 border-r border-slate-200 bg-slate-50 px-3 py-3 text-left font-bold dark:border-slate-700 dark:bg-slate-950">
                  Statut
                </th>
                <th className="px-3 py-3 text-left font-bold">Vote</th>
                <th className="px-3 py-3 text-left font-bold">Type</th>
                <th className="px-3 py-3 text-left font-bold">Période</th>
                <th className="px-3 py-3 text-center font-bold">Questions</th>
                <th className="px-3 py-3 text-center font-bold">Votants</th>
                <th className="px-3 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-white/60 text-sm dark:divide-slate-800">
              {votes.map((vote) => {
                const status = getVoteStatusMeta(vote);
                const type = getVoteTypeMeta(vote.type);
                const description = truncateText(stripHtml(vote.description), 140);

                return (
                  <tr key={vote.id} className={`transition ${status.row}`}>
                    <td
                      className={`sticky left-0 z-20 border-r border-slate-200 px-3 py-4 align-top dark:border-slate-700 ${status.row}`}
                    >
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${status.badge}`}
                      >
                        {status.label}
                      </span>
                    </td>

                    <td className="px-3 py-4 align-top">
                      <p className="line-clamp-2 font-bold text-slate-900 dark:text-white">
                        {vote.title || "Sans titre"}
                      </p>

                      {description ? (
                        <p
                          className="mt-1 line-clamp-2 text-xs leading-5 text-slate-600 dark:text-slate-300"
                          title={stripHtml(vote.description)}
                        >
                          {description}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-slate-400">
                          Aucune description
                        </p>
                      )}
                    </td>

                    <td className="px-3 py-4 align-top">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${type.badge}`}
                      >
                        {type.label}
                      </span>
                    </td>

                    <td className="px-3 py-4 align-top">
                      <div className="space-y-1 text-xs leading-5 text-slate-700 dark:text-slate-300">
                        <p>
                          <span className="font-bold">Début :</span>{" "}
                          {formatDateTimeFR(vote.start_time)}
                        </p>
                        <p>
                          <span className="font-bold">Fin :</span>{" "}
                          {formatDateTimeFR(vote.end_time)}
                        </p>
                      </div>
                    </td>

                    <td className="px-3 py-4 text-center align-top">
                      <button
                        type="button"
                        onClick={() => onQuestions(vote)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm font-black text-slate-900 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-900 dark:text-white dark:ring-slate-700 dark:hover:bg-slate-800"
                      >
                        <FaListUl className="text-indigo-600 dark:text-indigo-300" />
                        {vote.questionCount ?? 0}
                      </button>
                    </td>

                    <td className="px-3 py-4 text-center align-top">
                      <button
                        type="button"
                        onClick={() => onResults(vote)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm font-black text-slate-900 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-900 dark:text-white dark:ring-slate-700 dark:hover:bg-slate-800"
                      >
                        <FaChartBar className="text-violet-600 dark:text-violet-300" />
                        {vote.voterCount ?? 0}
                      </button>
                    </td>

                    <td className="px-3 py-4 align-top">
                      <div className="flex items-center justify-end gap-1.5">
                        <ActionButton
                          title="Modifier"
                          onClick={() => onEdit(vote)}
                          tone="primary"
                        >
                          <FaEdit />
                        </ActionButton>

                        <ActionButton
                          title="Questions"
                          onClick={() => onQuestions(vote)}
                          tone="info"
                        >
                          <FaPlus />
                        </ActionButton>

                        <ActionButton
                          title="Résultats"
                          onClick={() => onResults(vote)}
                          tone="violet"
                        >
                          <FaChartBar />
                        </ActionButton>

                        <ActionButton
                          title="Supprimer"
                          onClick={() => onDelete(vote)}
                          disabled={deletingId === vote.id}
                          tone="danger"
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}